<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { ref, onMounted, computed } from "vue";
import { useUserReportStore } from "@/stores/reportUserStore";
import UserReportTable from "./user-report-table.vue";

const title = ref("User Report");
const text = ref("HR");
const text1 = ref("User Report");

const store = useUserReportStore();

// ---------- computed data ----------
const loading = computed(() => store.loading || store.reportLoading);
const items = computed(() => store.items);
const meta = computed(() => store.meta);
const summary = computed(() => store.summary);
const chart = computed(() => store.chart);

const sortLabel = ref("Last 7 Days");
const dateRangeInput = ref(null);

// ---------- UI actions ----------
function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function applyFilters(page = 1, options = {}) {
  await Promise.all([
    store.list({ page, ...(options.force ? { force: true } : {}) }),
    store.fetchReport(options.force ? { force: true } : {}),
  ]);
}

function handlePageChange(page) {
  applyFilters(page);
}

function setStatus(status) {
  store.filters.status = status;
  applyFilters(1);
}

function setRole(role) {
  store.filters.role = role;
  applyFilters(1);
}

function setSort(sort, label) {
  store.filters.sort = sort;
  sortLabel.value = label;
  applyFilters(1);
}

function exportAs(format) {
  store.downloadReport(format);
}

// ---------- chart options ----------
const chartOptions = computed(() => ({
  chart: { type: "pie" },
  labels: chart.value.labels,
  legend: { position: "bottom" },
}));

const chartSeries = computed(() => chart.value.series || [0, 0]);

// ---------- date range ----------
onMounted(async () => {
  const start = moment().subtract(6, "days");
  const end = moment();

  store.filters.date_from = start.format("YYYY-MM-DD");
  store.filters.date_to = end.format("YYYY-MM-DD");

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      function (start, end) {
        dateRangeInput.value.value =
          start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        store.filters.date_from = start.format("YYYY-MM-DD");
        store.filters.date_to = end.format("YYYY-MM-DD");
        applyFilters(1);
      }
    );

    dateRangeInput.value.value =
      start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
  }

  await applyFilters(1);
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
                href="javascript:void(0)"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('pdf')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportAs('xlsx')"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a
              href="javascript:void(0)"
              id="collapse-header"
              @click="toggleHeader"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="row dq-stat-grid">
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill">
            <!-- Total Users -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <p class="fs-12 fw-medium mb-1 text-truncate">Total Users</p>
                  <h4>{{ summary.total_users }}</h4>
                </div>
              </div>
            </div>

            <!-- Active -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <p class="fs-12 fw-medium mb-1 text-truncate">Active Users</p>
                  <h4>{{ summary.active_users }}</h4>
                </div>
              </div>
            </div>

            <!-- New -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <p class="fs-12 fw-medium mb-1 text-truncate">New Users</p>
                  <h4>{{ summary.new_users }}</h4>
                </div>
              </div>
            </div>

            <!-- Inactive -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <p class="fs-12 fw-medium mb-1 text-truncate">
                    Inactive Users
                  </p>
                  <h4>{{ summary.inactive_users }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0">
              <h5>Users by Status</h5>
            </div>
            <div class="card-body pt-0">
              <apexchart
                type="pie"
                height="190"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Users List</h5>

          <div class="d-flex align-items-center flex-wrap row-gap-3 dq-list-toolbar">
            <!-- Date Range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Role -->
            <div class="dropdown me-3">
              <a
                class="dropdown-toggle btn btn-white"
                data-bs-toggle="dropdown"
                href="javascript:void(0)"
              >
                {{ store.filters.role || "Role" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item" @click.prevent="setRole('Employee')"
                    >Employee</a
                  >
                </li>
                <li>
                  <a class="dropdown-item" @click.prevent="setRole('Client')"
                    >Client</a
                  >
                </li>
                <li>
                  <a class="dropdown-item" @click.prevent="setRole('')">All</a>
                </li>
              </ul>
            </div>

            <!-- Status -->
            <div class="dropdown me-3">
              <a
                class="dropdown-toggle btn btn-white"
                data-bs-toggle="dropdown"
                href="javascript:void(0)"
              >
                {{ store.filters.status || "Status" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item" @click.prevent="setStatus('Active')"
                    >Active</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item"
                    @click.prevent="setStatus('Inactive')"
                    >Inactive</a
                  >
                </li>
                <li>
                  <a class="dropdown-item" @click.prevent="setStatus('')"
                    >All</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item"
                    @click.prevent="setSort('asc', 'Name (A to Z)')"
                    >Name (A to Z)</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item"
                    @click.prevent="setSort('desc', 'Name (Z to A)')"
                    >Name (Z to A)</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item"
                    @click.prevent="setSort('oldest', 'Oldest First')"
                    >Oldest First</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <UserReportTable
              :items="items"
              :loading="loading"
              :meta="meta"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p>2014 - 2025 © DoneQuick HRM.</p>
      <p>Powered by DoneQuick Technological World</p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>

